import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Alert
} from '@mui/material';
import {
  Assessment,
  AccessTime,
  TrendingUp,
  People,
  Add,
  Today,
  CheckCircle,
  Warning,
  Info,
  MoreVert,
  Refresh,
  Download,
  Storage,
  Chat
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import dataConnectionService from '../services/dataConnectionService';

const Dashboard: React.FC = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  // Chat mesaj sayısını yükle
  useEffect(() => {
    const loadChatCount = async () => {
      try {
        // Mock chat mesaj sayısı - gerçek implementasyonda data connection'dan gelecek
        const mockCount = Math.floor(Math.random() * 10) + 1;
        setUnreadChatCount(mockCount);
      } catch (error) {
        console.error('Chat mesaj sayısı yüklenirken hata:', error);
      }
    };

    loadChatCount();
  }, []);

  // Rol kontrolü - tüm kullanıcılar erişebilir
  if (!hasRole(['admin', 'developer', 'mod', 'call_admin', 'call', 'livechat_admin', 'livechat', 'risk_admin', 'risk'])) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır.
        </Alert>
      </Container>
    );
  }

  const quickActions = [
    {
      title: 'Rapor Oluştur',
      description: 'Yeni rapor oluştur',
      icon: <Add />,
      path: '/reports/create',
      roles: ['call', 'livechat', 'risk'],
      color: 'primary',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: 'Mesai Takibi',
      description: 'Mesai giriş/çıkış',
      icon: <AccessTime />,
      path: '/work-time',
      roles: ['call_admin', 'livechat_admin', 'risk_admin', 'call', 'livechat', 'risk'],
      color: 'success',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      title: 'Günlük Rapor',
      description: 'Günlük rapor görüntüle',
      icon: <Today />,
      path: '/reports/daily',
      roles: ['call_admin', 'livechat_admin', 'risk_admin'],
      color: 'info',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      title: 'Aylık Rapor',
      description: 'Aylık rapor görüntüle',
      icon: <Assessment />,
      path: '/reports/monthly',
      roles: ['call_admin', 'livechat_admin', 'risk_admin'],
      color: 'warning',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      title: 'Satış Raporları',
      description: 'Satış performansı',
      icon: <TrendingUp />,
      path: '/reports/sales',
      roles: ['call_admin', 'livechat_admin', 'risk_admin'],
      color: 'secondary',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    },
    {
      title: 'Kullanıcı Yönetimi',
      description: 'Kullanıcıları yönet',
      icon: <People />,
      path: '/users',
      roles: ['admin', 'developer'],
      color: 'error',
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
    },
    {
      title: 'Data Bağlantısı',
      description: 'Veri kaynağı ayarları',
      icon: <Storage />,
      path: '/settings/pages',
      roles: ['admin'],
      color: 'info',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: 'Canlı Chat',
      description: 'Ekip iletişimi',
      icon: <Chat />,
      path: '/chat',
      roles: ['admin', 'livechat_admin', 'call_admin', 'risk_admin'],
      color: 'success',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    }
  ];

  const filteredActions = quickActions.filter(action => 
    hasRole(action.roles)
  );

  // Data connection durumunu kontrol et
  const isDataConnected = dataConnectionService.isConnected();
  
  // Mock data for statistics (sadece data bağlantısı yoksa)
  const stats = isDataConnected ? {
    totalReports: 0, // Gerçek data'dan gelecek
    monthlyHours: 0,
    successfulSales: 0,
    failedSales: 0,
    activeUsers: 0,
    completionRate: 0
  } : {
    totalReports: 24,
    monthlyHours: 180,
    successfulSales: 15,
    failedSales: 3,
    activeUsers: 12,
    completionRate: 85
  };

  const recentActivities = isDataConnected ? [] : [
    { id: 1, action: 'Günlük rapor oluşturuldu', user: 'Ahmet Yılmaz', time: '2 saat önce', type: 'success' },
    { id: 2, action: 'Mesai girişi yapıldı', user: 'Fatma Demir', time: '3 saat önce', type: 'info' },
    { id: 3, action: 'Aylık rapor tamamlandı', user: 'Mehmet Kaya', time: '1 gün önce', type: 'success' },
    { id: 4, action: 'Yeni kullanıcı eklendi', user: 'Admin', time: '2 gün önce', type: 'warning' }
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle color="success" />;
      case 'info': return <Info color="info" />;
      case 'warning': return <Warning color="warning" />;
      default: return <Info color="info" />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Hoş Geldiniz, {user?.firstName}! 👋
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Panel yönetim sistemine hoş geldiniz. İşte bugünkü özetiniz.
            </Typography>
            
            {/* Data Connection Durumu */}
            {!isDataConnected && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Mock Veri Modu:</strong> Data bağlantısı kurulmadığı için örnek veriler gösteriliyor. 
                  Gerçek veriler için Sayfa Ayarları'ndan data bağlantısı kurun.
                </Typography>
              </Alert>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton color="primary">
              <Refresh />
            </IconButton>
            <IconButton color="primary">
              <Download />
            </IconButton>
          </Box>
        </Box>
        
        {/* User Info Card */}
        <Card sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          mb: 3
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                width: 60, 
                height: 60, 
                bgcolor: 'rgba(255,255,255,0.2)',
                fontSize: '1.5rem'
              }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Chip 
                  label={user?.role.toUpperCase()} 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 'bold'
                  }} 
                />
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  Son giriş: Bugün 09:30
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 3,
        mb: 4
      }}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          height: '100%',
          borderRadius: 3
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.totalReports}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Toplam Rapor
                </Typography>
              </Box>
              <Assessment sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          height: '100%',
          borderRadius: 3
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.monthlyHours}h
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Mesai Saati
                </Typography>
              </Box>
              <AccessTime sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          height: '100%',
          borderRadius: 3
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.successfulSales}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Başarılı Satış
                </Typography>
              </Box>
              <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          color: 'white',
          height: '100%',
          borderRadius: 3
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.completionRate}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Tamamlanma Oranı
                </Typography>
              </Box>
              <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3,
        mb: 3
      }}>
        {/* Quick Actions */}
        <Card sx={{ height: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Hızlı İşlemler
              </Typography>
              <Chip label={`${filteredActions.length} işlem`} color="primary" />
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              {filteredActions.map((action, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate(action.path)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      p: 1,
                      borderRadius: 2,
                      background: action.gradient,
                      color: 'white'
                    }}>
                      {action.icon}
                      <Typography variant="h6" component="h2" sx={{ ml: 1, fontWeight: 'bold' }}>
                        {action.title}
                      </Typography>
                      {action.title === 'Canlı Chat' && unreadChatCount > 0 && (
                        <Chip 
                          label={unreadChatCount} 
                          size="small" 
                          sx={{ 
                            ml: 'auto',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 'bold'
                          }} 
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {action.description}
                    </Typography>
                    <Button 
                      size="small" 
                      variant="contained"
                      fullWidth
                      sx={{ 
                        background: action.gradient,
                        '&:hover': {
                          background: action.gradient,
                          opacity: 0.9
                        }
                      }}
                    >
                      {action.title === 'Canlı Chat' ? 'Canlı Chat' : 
                       action.title === 'Data Bağlantısı' ? 'İşleme Git' : 'Başla'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Card sx={{ height: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Son Aktiviteler
              </Typography>
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            </Box>
            
            <Stack spacing={2}>
              {recentActivities.map((activity) => (
                <Box key={activity.id} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'grey.50',
                  '&:hover': {
                    bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100'
                  }
                }}>
                  {getActivityIcon(activity.type)}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium" color={mode === 'dark' ? 'text.primary' : undefined}>
                      {activity.action}
                    </Typography>
                    <Typography variant="caption" color={mode === 'dark' ? 'grey.400' : 'text.secondary'}>
                      {activity.user} • {activity.time}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Progress Section */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Bu Ay İlerleme
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Rapor Tamamlama</Typography>
              <Typography variant="body2" fontWeight="bold">{stats.completionRate}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={stats.completionRate} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }
              }} 
            />
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2
          }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {stats.successfulSales}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Başarılı Satış
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {stats.failedSales}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Başarısız Satış
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Dashboard;